import { atom } from 'jotai';
import type { Atom } from 'jotai';

import { audioPlayerStateAtom } from '@/features/AudioPlayer/atoms/core/primaryAtoms';
import { createLoadedQueueAtom, queueAtom } from './queueAtoms';
import { getTrackIdFromDocument } from '@/features/AudioPlayer/util/search-document-to-track';
import { log } from '@/lib/utils';

import type {
  AudioTrack,
  QueueContext,
  QueueState,
} from '@/features/AudioPlayer/types';
import type { SearchResponseHit, DocumentSchema } from '@music-vine/setlist';

/**
 * Derived atoms for automatic queue management from search results
 * These atoms ensure tracks are always in a queue before they can be played
 */

/**
 * Helper to transform search results into AudioTrack format
 */
export const transformSearchResultsToTracks = <T extends DocumentSchema>(
  results: SearchResponseHit<T>[]
): Map<number, AudioTrack> => {
  const tracks = new Map<number, AudioTrack>();

  for (const hit of results) {
    if (!hit?.document) {
      continue;
    }

    const doc = hit.document;

    // Use shared utility for consistent track ID extraction
    const trackId = getTrackIdFromDocument(doc);
    if (!trackId || tracks.has(trackId)) {
      continue;
    }

    const track: AudioTrack = {
      id: trackId,
      sfx_id: doc.sfx_id,
      track_id: doc.track_id ? Number(doc.track_id) : undefined,
      track_version_id: doc.track_version_id,
      sfx_variant_id: doc.sfx_variant_id,
      type: doc.asset_type === 'sfx' ? 'sfx' : 'music',
      name: doc.name ?? 'Unknown',
      slug: doc.track_slug || doc.sfx_slug,
      contributor_image: doc.contributor_image ?? doc.image,
      contributor_name: doc.contributor_name ?? 'Unknown Artist',
      contributor_slug: doc.contributor_slug,
      version_preview_uri: doc.version_preview_uri,
      version_length: doc.version_length,
      version_precise_length: doc.version_precise_length,
      is_premium: doc.is_premium,
      styles: doc.styles,
      tags: doc.tags,
    };

    tracks.set(trackId, track);
  }

  return tracks;
};

/**
 * Utility function to extract tracks from atomWithInfiniteQuery search results
 * This processes the search data and returns AudioTrack array for queue creation
 */
export const extractTracksFromSearchResult = <
  T extends DocumentSchema,
>(searchResult: {
  data?: {
    pages?: Array<{
      results?: Array<{
        hits?: SearchResponseHit<T>[];
      }>;
    } | null>;
  };
}): AudioTrack[] => {
  // Handle atomWithInfiniteQuery result structure
  if (!searchResult?.data?.pages) {
    return [];
  }

  // Process all pages into tracks
  const allTracks = new Map<number, AudioTrack>();

  for (const page of searchResult.data.pages) {
    if (!page) {
      continue;
    }

    // Handle the results array from MusicSearchResponse/SfxSearchResponse
    if ('results' in page && Array.isArray(page.results)) {
      for (const searchResponse of page.results) {
        if (!searchResponse?.hits) {
          continue;
        }

        const hits = searchResponse.hits;
        const pageTracks = transformSearchResultsToTracks(hits);

        // Merge into allTracks
        for (const [id, track] of pageTracks) {
          allTracks.set(id, track);
        }
      }
    }
  }

  return Array.from(allTracks.values());
};

/**
 * Atom that ensures a track exists in a queue before playing
 * Returns a function that creates a temporary queue if needed
 */
export const ensureTrackInQueueAtom = atom(
  null,
  (
    get,
    set,
    { trackId, tracks }: { trackId: number; tracks: AudioTrack[] }
  ) => {
    const state = get(audioPlayerStateAtom);

    // Check if track exists in current queue
    if (state.currentQueue.tracks.has(trackId)) {
      return true;
    }

    // Check if track exists in any loaded queue
    const inLoadedQueue = state.loadedQueues.some((q) => q.tracks.has(trackId));
    if (inLoadedQueue) {
      return true;
    }

    // Track not in any queue - create a temporary queue
    log('Track not in queue, creating temporary queue', { trackId });

    set(createLoadedQueueAtom, {
      tracks,
      context: {
        source: 'temporary',
        sourceType: 'browse' as const,
        sourceName: 'Current Page',
        sourceId: `temp-${Date.now()}`,
      },
      replace: false,
    });

    return true;
  }
);

/**
 * PURE DERIVED QUEUE ATOMS
 * These atoms create queue states directly from search result atoms
 * This is the new architecture that removes the need for useEffect bridges
 */

/**
 * Creates a pure derived queue atom from search results with interspersed ordering
 * This processes search results and returns a complete QueueState
 */
export const createPageQueueAtom = <T>(
  searchAtom: Atom<T>,
  context: QueueContext,
  processResults: (searchResult: T) => SearchResponseHit<DocumentSchema>[],
  orderedTrackIds?: number[]
) => {
  return atom((get): QueueState | null => {
    const searchResult = get(searchAtom);

    // Process search results using the provided function
    const results = processResults(searchResult);

    if (results.length === 0) {
      return null;
    }

    // Transform to tracks and create queue state
    const tracksMap = transformSearchResultsToTracks(results);
    const tracks = Array.from(tracksMap.values());

    if (tracks.length === 0) {
      return null;
    }

    // If an explicit track order is provided (e.g. from AI playlist generator),
    // use it instead of the default search result order
    const defaultOrder = tracks.map((track) => track.id);
    let trackOrder: number[];
    if (orderedTrackIds && orderedTrackIds.length > 0) {
      const filtered = orderedTrackIds.filter((id) => tracksMap.has(id));
      // Fall back to default order if no IDs matched
      trackOrder = filtered.length > 0 ? filtered : defaultOrder;
    } else {
      trackOrder = defaultOrder;
    }

    return {
      tracks: tracksMap,
      trackOrder,
      currentIndex: -1, // Not playing yet
      context,
      history: [],
      timestamp: Date.now(),
    };
  });
};

/**
 * Build a non-derived page queue atom from a precomputed set of search hits.
 *
 * `createPageQueueAtom` is the right tool when the source of truth is a live
 * jotai search atom (e.g. an `atomWithInfiniteQuery`). Some sections —
 * notably the per-asset-type "Latest" rows on the unified contributor page —
 * load their tracks through a plain TanStack `useQuery`, so there is no atom
 * to derive from. This helper takes the resolved hits directly and produces
 * a queue atom whose value matches what `createPageQueueAtom` would emit on
 * the same input.
 *
 * Callers must memoise on a stable `hits` reference so the returned atom
 * identity is stable across renders (Track components compare atoms by
 * reference when subscribing).
 */
export const createStaticPageQueueAtom = <T extends DocumentSchema>(
  hits: SearchResponseHit<T>[],
  context: QueueContext
): Atom<QueueState | null> => {
  const tracksMap = transformSearchResultsToTracks(hits);
  const tracks = Array.from(tracksMap.values());

  if (tracks.length === 0) {
    return atom<QueueState | null>(null);
  }

  const queueState: QueueState = {
    tracks: tracksMap,
    trackOrder: tracks.map((track) => track.id),
    currentIndex: -1,
    context,
    history: [],
    timestamp: Date.now(),
  };

  return atom<QueueState | null>(queueState);
};

/**
 * Global atom that holds the current page's queue atom
 * This allows components to access the page queue without prop drilling
 */
export const currentPageQueueAtom = atom<Atom<QueueState | null> | null>(null);
currentPageQueueAtom.debugLabel = 'currentPageQueueAtom';

/**
 * AUTO-PAGINATION OF THE PLAYING QUEUE (DEV-6133)
 *
 * The active play queue is a snapshot taken when the user pressed play. On its
 * own it never grows, so autoplay used to stop at the end of whatever pages
 * were loaded at click time. The atoms below let the player keep that snapshot
 * in sync with the live page queue and fetch further pages as playback nears
 * the end — but only while the user is still playing from the page they
 * started on (matched by queue context). Manually-built queues (boards, AI
 * playlists, similar, download history) carry a different context and are left
 * untouched.
 */

const sameQueueContext = (a: QueueContext, b: QueueContext): boolean =>
  a.sourceType === b.sourceType &&
  a.source === b.source &&
  a.sourceId === b.sourceId;

/**
 * Pagination controls for the current page's search, published by the search
 * hook so the global player can drive `fetchNextPage` without prop drilling.
 * `pageQueueAtom` ties these controls to the page-queue atom they belong to, so
 * a different page setting `currentPageQueueAtom` invalidates stale controls.
 */
export type PageQueuePagination = {
  pageQueueAtom: Atom<QueueState | null>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void | Promise<void>;
};

export const currentPagePaginationAtom = atom<PageQueuePagination | null>(null);
currentPagePaginationAtom.debugLabel = 'currentPagePaginationAtom';

/**
 * Live value of the current page's queue atom (unwraps the meta-atom). Updates
 * as more pages load, unlike the frozen active queue.
 */
export const currentPageQueueValueAtom = atom((get): QueueState | null => {
  const inner = get(currentPageQueueAtom);
  return inner ? get(inner) : null;
});
currentPageQueueValueAtom.debugLabel = 'currentPageQueueValueAtom';

/**
 * True once this page has audio the user could play — a page queue published by
 * a search hook, an active queue, or a loaded queue from a track/SFX page.
 *
 * The player itself is loaded lazily on first play (DEV-6487); this atom is the
 * signal to warm its chunk during idle time so the click still feels instant.
 * Pages with no audio never flip it, so they never fetch the chunk. Boolean-only
 * so it doesn't re-render subscribers as more pages of results load.
 */
export const pageHasPlayableAudioAtom = atom((get): boolean => {
  const pageQueue = get(currentPageQueueValueAtom);

  if (pageQueue && pageQueue.trackOrder.length > 0) {
    return true;
  }

  const playerState = get(audioPlayerStateAtom);

  return (
    playerState.currentQueue.trackOrder.length > 0 ||
    playerState.loadedQueues.some((queue) => queue.trackOrder.length > 0)
  );
});
pageHasPlayableAudioAtom.debugLabel = 'pageHasPlayableAudioAtom';

/**
 * True when the server has more pages to fetch for the queue that is currently
 * playing. Guards on (1) the published controls belonging to the live page
 * queue and (2) the active queue sharing the page queue's context — so it
 * never reports "more" for a manually-built queue or a stale page.
 */
export const activeQueueHasMorePagesAtom = atom((get): boolean => {
  const pagination = get(currentPagePaginationAtom);
  const inner = get(currentPageQueueAtom);

  if (
    !(pagination?.hasNextPage && inner) ||
    pagination.pageQueueAtom !== inner
  ) {
    return false;
  }

  const pageQueue = get(inner);
  if (!pageQueue) {
    return false;
  }

  const active = get(audioPlayerStateAtom).currentQueue;
  return sameQueueContext(active.context, pageQueue.context);
});
activeQueueHasMorePagesAtom.debugLabel = 'activeQueueHasMorePagesAtom';

/**
 * Set while autoplay has run off the end of the loaded tracks and is waiting
 * for the next page to arrive before advancing. Cleared once the page is
 * appended (and playback resumes) or once it's clear no more pages exist.
 */
export const waitingForNextPageAtom = atom(false);
waitingForNextPageAtom.debugLabel = 'waitingForNextPage';

/**
 * Append any tracks from the live page queue that aren't yet in the active
 * queue, preserving the active queue's order and `currentIndex` so playback is
 * never interrupted. No-op unless the active queue shares the page queue's
 * context (i.e. the user is still playing from the page they started on).
 */
export const syncActiveQueueWithPageAtom = atom(null, (get, set) => {
  const inner = get(currentPageQueueAtom);
  const pageQueue = inner ? get(inner) : null;

  if (!pageQueue) {
    return;
  }

  const active = get(audioPlayerStateAtom).currentQueue;

  if (!sameQueueContext(active.context, pageQueue.context)) {
    return;
  }

  const newTracks = new Map(active.tracks);
  const newTrackOrder = [...active.trackOrder];
  let appended = 0;

  for (const trackId of pageQueue.trackOrder) {
    if (!newTracks.has(trackId)) {
      const track = pageQueue.tracks.get(trackId);
      if (track) {
        newTracks.set(trackId, track);
        newTrackOrder.push(trackId);
        appended += 1;
      }
    }
  }

  if (appended === 0) {
    return;
  }

  set(queueAtom, {
    ...active,
    tracks: newTracks,
    trackOrder: newTrackOrder,
  });

  log('Extended active queue with newly-loaded page tracks', {
    appended,
    total: newTrackOrder.length,
    context: active.context.source,
  });
});
syncActiveQueueWithPageAtom.debugLabel = 'syncActiveQueueWithPage';
